import { useEffect } from 'react';
import { format } from 'date-fns';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useTranslation } from 'react-i18next';
import type { AuditEvent } from '@/schemas/audit';

interface Props {
  event: AuditEvent | null;
  open: boolean;
  onClose: () => void;
}

function DiffView({
  before,
  after,
}: {
  before?: Record<string, unknown> | undefined;
  after?: Record<string, unknown> | undefined;
}) {
  const { t } = useTranslation();
  const aKeys = Object.keys(after ?? {});
  const bKeys = Object.keys(before ?? {});
  const all = Array.from(new Set([...bKeys, ...aKeys])).sort();
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="text-xs font-semibold text-muted-foreground">
          {t('audit.drawer.before')}
        </div>
        <div className="text-xs font-semibold text-muted-foreground">{t('audit.drawer.after')}</div>
      </div>
      {all.map((k) => {
        const bv = JSON.stringify((before ?? {})[k] ?? null);
        const av = JSON.stringify((after ?? {})[k] ?? null);
        const changed = bv !== av;
        return (
          <div
            key={k}
            className={`grid grid-cols-2 gap-2 rounded-md border p-2 font-mono text-xs ${
              changed ? 'border-status-pending/30 bg-status-pending/10' : ''
            }`}
          >
            <div className="break-all text-muted-foreground">{bv}</div>
            <div
              className={`break-all ${changed ? 'text-status-active' : 'text-muted-foreground'}`}
            >
              {av}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MetadataView({ event }: { event: AuditEvent }) {
  const { t } = useTranslation();
  const meta = event.metadata ?? {};
  const keys = Object.keys(meta);
  if (keys.length === 0)
    return <p className="text-sm text-muted-foreground">{t('audit.drawer.noMetadata')}</p>;
  return (
    <div className="space-y-2">
      {keys.map((k) => (
        <div key={k} className="flex flex-col gap-0.5">
          <span className="text-xs font-medium text-muted-foreground">{k}</span>
          <span className="break-all font-mono text-xs">{JSON.stringify(meta[k])}</span>
        </div>
      ))}
    </div>
  );
}

export function AuditDrawer({ event, open, onClose }: Props) {
  const { t } = useTranslation();
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const rawEvent = event?.event ?? '';
  const eventKey = `common.events.${rawEvent.toLowerCase()}`;
  const eventWord = event ? (t(eventKey) === eventKey ? rawEvent : t(eventKey)) : '';
  const title = event ? `${eventWord} ${event.serial}` : '';
  const ts = event?.createdAt ? new Date(event.createdAt) : null;
  const tsStr = ts ? format(ts, 'yyyy-MM-dd HH:mm:ss') : '';

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[480px] max-w-full sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span className="truncate">{title}</span>
          </SheetTitle>
          {ts && <span className="font-mono text-xs text-faint">{tsStr}</span>}
        </SheetHeader>

        <ScrollArea className="mt-4 h-[calc(100vh-140px)] pr-2">
          {event && event.event === 'updated' && (event.before || event.after) ? (
            <div>
              <h4 className="mb-2 text-sm font-semibold">{t('audit.drawer.changes')}</h4>
              <DiffView before={event.before} after={event.after} />
              <Separator className="my-4" />
              <h4 className="mb-2 text-sm font-semibold">{t('audit.drawer.fullPayload')}</h4>
              <pre className="overflow-auto rounded-md border bg-foreground/[0.02] p-3 font-mono text-xs">
                {JSON.stringify({ ...event }, null, 2)}
              </pre>
            </div>
          ) : event ? (
            <div className="space-y-4">
              <div>
                <h4 className="mb-2 text-sm font-semibold">{t('audit.drawer.metadata')}</h4>
                <MetadataView event={event} />
              </div>
              <Separator />
              <div>
                <h4 className="mb-2 text-sm font-semibold">{t('audit.drawer.fullPayload')}</h4>
                <pre className="overflow-auto rounded-md border bg-foreground/[0.02] p-3 font-mono text-xs">
                  {JSON.stringify({ ...event }, null, 2)}
                </pre>
              </div>
            </div>
          ) : null}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
